/**
 * Servicio API del area de Alianzas
 * Ubicacion: src/services/alianzasApi.js
 */

import axios from 'axios';

const getApiBaseUrl = () => {
  const baseUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  return `${baseUrl}/alianzas`;
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 45000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Token vencido o invalido: se limpia la sesion y se vuelve al login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Normaliza el mensaje de error de FastAPI para mostrarlo en la UI
export const apiErrorMessage = (error, fallback = 'Ocurrio un error inesperado') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) return detail[0]?.msg || fallback;
  return error?.message || fallback;
};

export const alianzasApi = {
  getEnums: () => api.get('/meta/enums').then(r => r.data),
  getUsuarios: () => api.get('/usuarios').then(r => r.data),

  // Asignaciones
  listAsignaciones: (params = {}) =>
    api.get('/asignaciones', { params, paramsSerializer: { indexes: null } }).then(r => r.data),
  getResumen: () => api.get('/asignaciones/resumen').then(r => r.data),
  createAsignacion: (payload) => api.post('/asignaciones', payload).then(r => r.data),
  updateAsignacion: (id, payload) => api.put(`/asignaciones/${id}`, payload).then(r => r.data),
  deleteAsignacion: (id) => api.delete(`/asignaciones/${id}`).then(r => r.data),

  // Comentarios
  // origen: 'asignacion' | 'sesion', segun sobre que entidad se comenta.
  listComentarios: (origen, id) =>
    api.get(`/${origen === 'sesion' ? 'sesiones' : 'asignaciones'}/${id}/comentarios`).then(r => r.data),
  createComentario: (origen, id, payload) =>
    api.post(`/${origen === 'sesion' ? 'sesiones' : 'asignaciones'}/${id}/comentarios`, payload).then(r => r.data),
  updateComentario: (comentarioId, payload) =>
    api.put(`/comentarios/${comentarioId}`, payload).then(r => r.data),
  deleteComentario: (comentarioId) =>
    api.delete(`/comentarios/${comentarioId}`).then(r => r.data),
  subirAdjunto: (file) => {
    const form = new FormData();
    form.append('archivo', file);
    // Sin Content-Type explicito: el navegador arma el boundary del multipart.
    return api.post('/adjuntos', form, { headers: { 'Content-Type': undefined } })
      .then(r => r.data);
  },

  // Sesiones / capacitaciones
  listSesiones: (params = {}) =>
    api.get('/sesiones', { params, paramsSerializer: { indexes: null } }).then(r => r.data),
  createSesion: (payload) => api.post('/sesiones', payload).then(r => r.data),
  updateSesion: (id, payload) => api.put(`/sesiones/${id}`, payload).then(r => r.data),
  deleteSesion: (id) => api.delete(`/sesiones/${id}`).then(r => r.data),
  getCalendario: (params) => api.get('/sesiones/calendario', { params }).then(r => r.data),

  // Gestión de datos del QBR
  listQbr: (entidad, params = {}) =>
    api.get(`/qbr/${entidad}`, { params }).then(r => r.data),
  createQbr: (entidad, payload) => api.post(`/qbr/${entidad}`, payload).then(r => r.data),
  updateQbr: (entidad, id, payload) => api.put(`/qbr/${entidad}/${id}`, payload).then(r => r.data),
  deleteQbr: (entidad, id) => api.delete(`/qbr/${entidad}/${id}`).then(r => r.data),
};

export default alianzasApi;
